import { useEffect, useState } from 'react';
import type { Me } from './api/client.js';
import { useSession } from './useSession.js';
import { SetupPanel } from './views/SetupPanel.js';
import { PeoplePanel } from './views/PeoplePanel.js';
import { RotaPanel } from './views/RotaPanel.js';
import { AccessPanel } from './views/AccessPanel.js';
import { ReviewView } from './views/ReviewView.js';

const TABS = ['Setup', 'People', 'Rotation', 'Operators', 'Review'] as const;
type Tab = (typeof TABS)[number];

type Summary = { eventId: string; title: string; state: string; updatedAt: string };

/** Admin console: opened in a normal browser after signing in at crtq.ai. */
export function AdminApp({ me }: { me: Me }) {
  const { session } = useSession();
  const rec = session.engine?.record ?? null;
  const [events, setEvents] = useState<Summary[]>([]);
  const [title, setTitle] = useState('Residency careers evening');
  const [tab, setTab] = useState<Tab>('Setup');
  const [msg, setMsg] = useState<string | null>(null);

  const refresh = () => void session.client.listEvents().then((r) => setEvents(r.events)).catch((e: unknown) => setMsg(String(e)));
  useEffect(() => {
    if (!rec) refresh();
  }, [rec?.eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const signOut = async () => {
    await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' } });
    location.reload();
  };

  return (
    <div>
      <h1>Rotation Controller · Admin</h1>
      <p className="muted">
        Signed in with CrtQ as {me.name || me.email}. <button className="secondary" onClick={() => void signOut()}>Sign out</button>
      </p>
      {session.error && <div className="banner banner-error" role="alert">{session.error}</div>}
      {msg && <div className="banner banner-error" role="alert">{msg}</div>}

      {!rec && (
        <div>
          <h2>Events</h2>
          <div className="row">
            <label htmlFor="title">New event</label>
            <input id="title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
            <button
              className="primary"
              onClick={() => {
                setMsg(null);
                void session.createEvent(title, 'Europe/London').then(() => setTab('Setup')).catch((e: unknown) => setMsg(String(e)));
              }}
            >
              Create event
            </button>
          </div>
          {events.length === 0 && <p className="muted">No events yet.</p>}
          {events.length > 0 && (
            <table>
              <thead><tr><th>Event</th><th>State</th><th>Updated</th><th /></tr></thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.eventId}>
                    <td>{e.title}</td>
                    <td>{e.state}</td>
                    <td>{new Date(e.updatedAt).toLocaleString()}</td>
                    <td className="row">
                      <button className="secondary" onClick={() => void session.loadEvent(e.eventId, false, true).then(() => setTab('Setup')).catch((x: unknown) => setMsg(String(x)))}>Open</button>
                      <a href={session.client.exportUrl(e.eventId)}>JSON</a>
                      <a href={`${session.client.exportUrl(e.eventId)}?format=csv`}>CSV</a>
                      <button
                        className="secondary"
                        onClick={() => {
                          if (confirm(`Delete "${e.title}" and everything recorded for it?`)) void session.client.deleteEvent(e.eventId).then(refresh).catch((x: unknown) => setMsg(String(x)));
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {rec && (
        <div>
          <p>
            <button className="secondary" onClick={() => void session.closeEvent()}>← All events</button> {rec.title} · state {rec.state} · schedule{' '}
            {rec.scheduleVersion ? `v${rec.scheduleVersion.version} (${rec.scheduleVersion.hash})` : 'none'}
          </p>
          {!session.hasControl && (
            <div className="banner banner-info">
              You are viewing this event. {session.leaseHolder ? `${session.leaseHolder} is controlling it. ` : ''}Setup, people and sign-up links can be changed any time; to change the rota itself, take control first.{' '}
              <button className="secondary" onClick={() => void session.takeControl()}>Take control</button>
            </div>
          )}
          <nav className="tabs" role="tablist" aria-label="Admin views">
            {TABS.map((t) => (
              <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}>{t}</button>
            ))}
          </nav>
          {tab === 'Setup' && <SetupPanel session={session} />}
          {tab === 'People' && <PeoplePanel session={session} />}
          {tab === 'Rotation' && <RotaPanel session={session} />}
          {tab === 'Operators' && <AccessPanel session={session} />}
          {tab === 'Review' && <ReviewView session={session} />}
        </div>
      )}
    </div>
  );
}
